/**
 * Lightweight IndexedDB cache for summaries — lets users keep reading their
 * history and individual summaries when offline (train, plane, dead-zone).
 *
 * Design: two object stores, both keyed by string id.
 *   - "list"    → single row keyed "all", holds the last successful GET /summaries payload
 *   - "details" → one row per summary id, holds the full detail payload
 *
 * On logout we blow the whole DB away so switching accounts on a shared
 * device never leaks cached content.
 */
const DB_NAME = "sux1-cache";
const DB_VERSION = 1;
const STORE_LIST = "list";
const STORE_DETAILS = "details";
const LIST_KEY = "all";

function open() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_LIST)) db.createObjectStore(STORE_LIST);
      if (!db.objectStoreNames.contains(STORE_DETAILS)) db.createObjectStore(STORE_DETAILS);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function txPut(store, key, value) {
  try {
    const db = await open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(store, "readwrite");
      tx.objectStore(store).put(value, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    return false;
  }
}

async function txGet(store, key) {
  try {
    const db = await open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(store, "readonly");
      const req = tx.objectStore(store).get(key);
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

async function txDelete(store, key) {
  try {
    const db = await open();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(store, "readwrite");
      tx.objectStore(store).delete(key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    return false;
  }
}

// -------- Public API --------

export async function cacheList(items) {
  if (!Array.isArray(items)) return;
  await txPut(STORE_LIST, LIST_KEY, {
    items,
    cached_at: new Date().toISOString(),
  });
}

export async function loadCachedList() {
  const row = await txGet(STORE_LIST, LIST_KEY);
  if (!row || !Array.isArray(row.items)) return null;
  return row; // { items, cached_at }
}

export async function cacheDetail(summary) {
  if (!summary || !summary.id) return;
  await txPut(STORE_DETAILS, summary.id, {
    summary,
    cached_at: new Date().toISOString(),
  });
}

export async function loadCachedDetail(id) {
  const row = await txGet(STORE_DETAILS, id);
  if (!row || !row.summary) return null;
  return row; // { summary, cached_at }
}

export async function forgetDetail(id) {
  await txDelete(STORE_DETAILS, id);
}

export async function clearAll() {
  try {
    const db = await open();
    await new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_LIST, STORE_DETAILS], "readwrite");
      tx.objectStore(STORE_LIST).clear();
      tx.objectStore(STORE_DETAILS).clear();
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* best-effort */
  }
}
